import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@gafe/common-ui';
import merge from 'classnames';
import { find } from 'lodash';

import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { ContactVm, MediaType } from '@/types';

import { ContactAddress } from '../../types';
import { AddressBlock } from '../Address/AddressBlock';
import { AddressRow } from '../Address/AddressRow';

import { Props } from './types';

import styles from './AddressList.module.scss';

export const AddressList = (props: Props) => {
  const mediaType = useDeviceDetection();
  const {
    addresses,
    deleteError,
    initiallySelectedAddress,
    selectAddress,
    deleteAddress,
    openForm,
    closeModal,
  } = props;

  const [selectedAddress, setSelectedAddress] = useState<ContactVm | null>(
    initiallySelectedAddress,
  );

  // set the selected address to the selected address passed from parent
  // when the initially selected address is a warehouse address
  useEffect(() => {
    const isWarehouseAddressInitiallySelected =
      Boolean(initiallySelectedAddress) &&
      Boolean(find(addresses, a => a.contact.contactId === initiallySelectedAddress?.contactId));

    if (isWarehouseAddressInitiallySelected) {
      setSelectedAddress(initiallySelectedAddress);
    }
  }, [initiallySelectedAddress]);

  const onUseAddressButtonClick = useCallback(() => {
    if (selectedAddress) {
      selectAddress(selectedAddress);
      closeModal();
    }
  }, [selectedAddress, selectAddress, closeModal]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>Address Book</div>
      {addresses.length !== 0 && (
        <div className={styles.sub_title}>
          Choose an existing address or <span onClick={() => openForm()}>add a new one</span>.
        </div>
      )}
      {addresses.length === 0 && (
        <div className={styles.sub_title}>
          <span onClick={() => openForm()}>Add a new saved address</span> to your address book.
        </div>
      )}
      <div className={merge(styles.address_content, styles.full_width)}>
        <div className={merge(styles.cards_block, styles.full_width)}>
          {addresses.length !== 0 && (
            <div className={merge(styles.cards_wrapper, styles.flex_column)}>
              {mediaType !== MediaType.Mobile &&
                addresses.map((address: ContactAddress) => (
                  <AddressRow
                    address={address}
                    deleteAddress={() => deleteAddress(address.contact)}
                    editAddress={() => openForm(address.contact)}
                    isSelected={address.contact.contactId === selectedAddress?.contactId}
                    key={address.title}
                    selectAddress={() => setSelectedAddress(address.contact)}
                  />
                ))}
              {mediaType === MediaType.Mobile &&
                addresses.map((address: ContactAddress) => (
                  <AddressBlock
                    address={address}
                    deleteAddress={() => deleteAddress(address.contact)}
                    editAddress={() => openForm(address.contact)}
                    isSelected={address.contact.contactId === selectedAddress?.contactId}
                    key={address.title}
                    selectAddress={() => setSelectedAddress(address.contact)}
                    withActionButtons
                  />
                ))}
            </div>
          )}
          <div className={styles.add_address} onClick={() => openForm()}>
            Add new address
          </div>
          {deleteError.length !== 0 && <div className={styles.delete_error}>{deleteError}</div>}
          <div className={styles.stepper_buttons}>
            <Button colorSchema="neutral" onClick={closeModal} size="large" variant="text">
              Cancel
            </Button>
            <Button
              colorSchema="primary"
              disabled={addresses.length === 0 || !selectedAddress}
              onClick={onUseAddressButtonClick}
              size="large"
              testId="use-address-button"
              variant="contained"
            >
              Use Selected Address
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
